import { Controller, useFormContext } from 'react-hook-form';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Stack,
  TextField,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useEffect } from 'react';

const weekdays = [
  { value: 'mo', label: 'Monday' },
  { value: 'tu', label: 'Tuesday' },
  { value: 'we', label: 'Wednesday' },
  { value: 'th', label: 'Thursday' },
  { value: 'fr', label: 'Friday' },
  { value: 'sa', label: 'Saturday' },
  { value: 'su', label: 'Sunday' },
];

function RecurrenceRuleSection() {
  const { control, watch, setValue } = useFormContext();
  const freq = watch('rrule.freq');
  watch('rrule');

  useEffect(() => {
    if (freq !== 'weekly') {
      setValue('rrule.byweekday', []);
    }
    if (['daily', 'monthly'].includes(freq)) {
      setValue('rrule.interval', 1);
    }
  }, [freq, setValue]);

  return (
    <>
      <Stack spacing={2} direction='row'>
        <Controller
          name='rrule.freq'
          control={control}
          render={({ field }) => (
            <FormControl
              sx={{
                flex: 1,
              }}
            >
              <InputLabel id='select-freq'>Frequency</InputLabel>
              <Select
                {...field}
                value={field.value || ''}
                labelId='select-freq'
                label='Frequency'
              >
                <MenuItem value=''>None</MenuItem>
                <MenuItem value='daily'>Daily</MenuItem>
                <MenuItem value='weekly'>Weekly</MenuItem>
                <MenuItem value='monthly'>Monthly</MenuItem>
              </Select>
            </FormControl>
          )}
        />

        {freq === 'daily' && (
          <Controller
            name='rrule.interval'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value || 1}
                label='Interval (e.g., every X days)'
                type='number'
                inputProps={{
                  min: 1,
                  max: 90,
                }}
                sx={{ flex: 1 }}
                placeholder='Enter a number between 1 and 90'
                variant='outlined'
              />
            )}
          />
        )}

        {freq === 'weekly' && (
          <Controller
            name='rrule.byweekday'
            control={control}
            render={({ field }) => (
              <FormControl sx={{ flex: 1 }}>
                <InputLabel id='select-day-freq-weekly'>
                  Days of the Week
                </InputLabel>
                <Select
                  {...field}
                  multiple
                  labelId='select-day-freq-weekly'
                  label='Days of the Week'
                  value={field.value}
                  renderValue={(selected) =>
                    (selected as string[])
                      .map(
                        (day) => weekdays.find((w) => w.value === day)?.label,
                      )
                      .join(', ')
                  }
                >
                  {weekdays.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      <Checkbox checked={field.value?.includes(day.value)} />
                      <ListItemText primary={day.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        )}

        {freq === 'monthly' && (
          <Controller
            name='rrule.interval'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value || 1}
                label='Interval (e.g., every X month(s))'
                type='number'
                inputProps={{
                  min: 1,
                  max: 12,
                }}
                sx={{ flex: 1 }}
                placeholder='Enter a number between 1 and 12'
                variant='outlined'
              />
            )}
          />
        )}
      </Stack>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {freq && (
          <Controller
            name='rrule.until'
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  {...field}
                  value={
                    field.value &&
                    moment(field.value, moment.ISO_8601, true).isValid()
                      ? moment.utc(field.value)
                      : null
                  }
                  onChange={(date) => field.onChange(date?.toISOString() || '')}
                  label='Until (End Date)'
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: 'Select an end date',
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        )}
      </Stack>
    </>
  );
}

export default RecurrenceRuleSection;
